import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { askAdminAi, generateAiTeluguBroadcast, sendAdminBroadcast, auditUserRiskAi } from '../services/firebase';
import MarkdownRenderer from '../components/MarkdownRenderer';

const QUICK_PROMPTS = [
  { label: '📊 Daily Briefing', prompt: 'Give me a complete daily executive briefing of today operations, driver health, and revenue.' },
  { label: '💰 Pending Commission Dues', prompt: 'Show all Annas who have outstanding platform commission dues and recommend collection priorities.' },
  { label: '🚩 Cancellation Root Causes', prompt: 'Analyze all cancelled rides today, identify why passengers or drivers cancelled, and suggest operational fixes.' },
  { label: '🏆 Top Fleet Champions', prompt: 'Identify the top performing drivers by completed trips, highest ratings, and reliability.' },
  { label: '🔥 Piler Rush Hour Forecast', prompt: 'What are the peak demand hours and high-traffic pickup hotspots in Piler today?' },
];

const INITIAL_CHAT = [
  {
    role: 'ai',
    text: `### 🤖 Welcome to **Admin AI Super Tool**!\n\nI am your live Fleet Intelligence & Operations Copilot powered by **Gemini 3.6 Flash**.\n\n* **Ask me anything** about rides, driver earnings, dues, cancellations, or Piler demand.\n* Or click one of the quick chips below to get started.`,
  },
];

const CHAT_STORAGE_KEY = 'mytownly_admin_ai_chat_history_v2';

function getStoredChat() {
  try {
    const raw = localStorage.getItem(CHAT_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch (e) {}
  return INITIAL_CHAT;
}

export default function AdminAiCopilot({ drivers = [], rides = [], users = [], isSuperAdmin = false }) {
  const [activeTab, setActiveTab] = useState('copilot'); // 'copilot' | 'broadcast' | 'fraud'
  
  // Copilot State
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState(getStoredChat);
  const chatBottomRef = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(chatHistory));
    } catch (e) {}
  }, [chatHistory]);

  const handleClearChat = () => {
    setChatHistory(INITIAL_CHAT);
    try {
      localStorage.removeItem(CHAT_STORAGE_KEY);
    } catch (e) {}
    toast.success('Chat history cleared');
  };

  // Broadcast State
  const [broadcastPrompt, setBroadcastPrompt] = useState('');
  const [broadcastLoading, setBroadcastLoading] = useState(false);
  const [generatedBroadcast, setGeneratedBroadcast] = useState(null);
  const [sendingBroadcast, setSendingBroadcast] = useState(false);

  // Fraud Audit State
  const [selectedUser, setSelectedUser] = useState('');
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditResult, setAuditResult] = useState(null);

  const handleSendPrompt = async (textToSend) => {
    const q = textToSend || prompt;
    if (!q.trim() || loading) return;
    setPrompt('');
    setLoading(true);

    setChatHistory((prev) => [...prev, { role: 'user', text: q }]);

    try {
      const res = await askAdminAi(q);
      setChatHistory((prev) => [
        ...prev,
        {
          role: 'ai',
          text: res.markdown,
          stats: res.stats,
        },
      ]);
    } catch (err) {
      toast.error('AI query failed: ' + (err.message || 'Please try again'));
      setChatHistory((prev) => [
        ...prev,
        { role: 'ai', text: '⚠️ **Error**: Failed to generate AI response. Please check your network or try again.' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateBroadcast = async () => {
    if (!broadcastPrompt.trim() || broadcastLoading) return;
    setBroadcastLoading(true);
    setGeneratedBroadcast(null);
    try {
      const res = await generateAiTeluguBroadcast(broadcastPrompt);
      setGeneratedBroadcast(res);
      toast.success('Telugu broadcast generated!');
    } catch (err) {
      toast.error('Failed to generate broadcast: ' + err.message);
    } finally {
      setBroadcastLoading(false);
    }
  };

  const handleSendFCM = async () => {
    if (!isSuperAdmin || !generatedBroadcast?.teluguText || sendingBroadcast) return;
    setSendingBroadcast(true);
    try {
      await sendAdminBroadcast({
        target: 'drivers',
        title: generatedBroadcast.shortTitle || 'ముఖ్యమైన సమాచారం',
        body: generatedBroadcast.teluguText,
        language: 'te',
      });
      toast.success('📢 Broadcast sent to all online Annas!');
      setGeneratedBroadcast(null);
      setBroadcastPrompt('');
    } catch (err) {
      toast.error('Failed to send broadcast: ' + err.message);
    } finally {
      setSendingBroadcast(false);
    }
  };

  const handleRunAudit = async (uId) => {
    const userToAudit = users.find((u) => u.id === uId) || { id: uId, name: 'Sample User', prankCancellationStrikes: 3, isFlaggedForPrank: true };
    setAuditLoading(true);
    setAuditResult(null);
    try {
      const res = await auditUserRiskAi({
        userId: userToAudit.id,
        name: userToAudit.name,
        phone: userToAudit.phone,
        strikes: userToAudit.prankCancellationStrikes || 0,
        isFlagged: userToAudit.isFlaggedForPrank || false,
        totalRides: rides.filter((r) => r.customerId === userToAudit.id).length,
      });
      setAuditResult(res);
      toast.success('Risk audit complete');
    } catch (err) {
      toast.error('Audit failed: ' + err.message);
    } finally {
      setAuditLoading(false);
    }
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>🤖</span> Admin AI Super Tool
            <span style={{ fontSize: '12px', background: 'linear-gradient(135deg, #00d68f, #00b377)', color: '#000', padding: '3px 8px', borderRadius: '12px', fontWeight: '700' }}>
              GEMINI 3.6 FLASH
            </span>
          </h1>
          <p style={{ margin: '4px 0 0', color: '#888', fontSize: '14px' }}>
            Autonomous Fleet Intelligence, 1-Click Telugu Driver Broadcasts, and Real-Time Fraud Sentinel
          </p>
        </div>

        {/* Tab Navigation */}
        <div style={{ display: 'flex', background: '#1a1f26', padding: '4px', borderRadius: '10px', border: '1px solid #2a313d' }}>
          <button
            onClick={() => setActiveTab('copilot')}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '13px',
              background: activeTab === 'copilot' ? '#00d68f' : 'transparent',
              color: activeTab === 'copilot' ? '#000' : '#888',
              transition: 'all 0.2s',
            }}
          >
            💬 Fleet Copilot
          </button>
          <button
            onClick={() => setActiveTab('broadcast')}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '13px',
              background: activeTab === 'broadcast' ? '#00d68f' : 'transparent',
              color: activeTab === 'broadcast' ? '#000' : '#888',
              transition: 'all 0.2s',
            }}
          >
            📢 Telugu Broadcast Studio
          </button>
          <button
            onClick={() => setActiveTab('fraud')}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '13px',
              background: activeTab === 'fraud' ? '#00d68f' : 'transparent',
              color: activeTab === 'fraud' ? '#000' : '#888',
              transition: 'all 0.2s',
            }}
          >
            🛡️ Fraud Sentinel
          </button>
        </div>
      </div>

      {/* TAB 1: FLEET COPILOT */}
      {activeTab === 'copilot' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px' }}>
          {/* Quick Chips */}
          <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '4px', alignItems: 'center' }}>
            {QUICK_PROMPTS.map((chip, idx) => (
              <button
                key={idx}
                onClick={() => handleSendPrompt(chip.prompt)}
                disabled={loading}
                style={{
                  background: '#1a1f26',
                  color: '#e0e0e0',
                  border: '1px solid #2a313d',
                  borderRadius: '20px',
                  padding: '8px 14px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#00d68f')}
                onMouseLeave={(e) => (e.currentTarget.style.borderColor = '#2a313d')}
              >
                {chip.label}
              </button>
            ))}

            {chatHistory.length > 1 && (
              <button
                onClick={handleClearChat}
                style={{
                  background: 'rgba(239, 68, 68, 0.1)',
                  color: '#ef4444',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '20px',
                  padding: '8px 14px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  marginLeft: 'auto',
                }}
              >
                🧹 Clear History
              </button>
            )}
          </div>

          {/* Chat Container */}
          <div
            style={{
              background: '#12161c',
              border: '1px solid #222a36',
              borderRadius: '16px',
              padding: '20px',
              minHeight: '420px',
              maxHeight: '600px',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            {chatHistory.map((msg, i) => (
              <div
                key={i}
                style={{
                  alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '85%',
                  background: msg.role === 'user' ? '#00d68f' : '#1a202c',
                  color: msg.role === 'user' ? '#000' : '#f0f0f0',
                  padding: '14px 18px',
                  borderRadius: '14px',
                  lineHeight: '1.6',
                  fontSize: '14px',
                  border: msg.role === 'user' ? 'none' : '1px solid #2d3748',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                }}
              >
                {msg.role === 'ai' ? (
                  <div>
                    <MarkdownRenderer content={msg.text} />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(msg.text);
                          toast.success('Report copied to clipboard!');
                        }}
                        style={{
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid #30363d',
                          color: '#94a3b8',
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontSize: '11px',
                          cursor: 'pointer',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px',
                        }}
                      >
                        📋 Copy Report
                      </button>
                    </div>
                  </div>
                ) : (
                  <strong>{msg.text}</strong>
                )}
              </div>
            ))}

            {loading && (
              <div
                style={{
                  alignSelf: 'flex-start',
                  background: '#1a202c',
                  padding: '12px 18px',
                  borderRadius: '14px',
                  border: '1px solid #2d3748',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: '#00d68f',
                  fontSize: '13px',
                }}
              >
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#00d68f', animation: 'pulse 1s infinite' }} />
                Analyzing live fleet data with Gemini 3.6 Flash...
              </div>
            )}
          </div>

          {/* Input Box */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSendPrompt();
            }}
            style={{ display: 'flex', gap: '10px' }}
          >
            <input
              type="text"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Ask anything about fleet operations, revenue, cancellations, or drivers in Piler..."
              disabled={loading}
              style={{
                flex: 1,
                background: '#161b22',
                border: '1px solid #30363d',
                borderRadius: '12px',
                padding: '14px 18px',
                color: '#fff',
                fontSize: '14px',
                outline: 'none',
              }}
            />
            <button
              type="submit"
              disabled={loading || !prompt.trim()}
              style={{
                background: loading || !prompt.trim() ? '#2a313d' : '#00d68f',
                color: loading || !prompt.trim() ? '#888' : '#000',
                border: 'none',
                borderRadius: '12px',
                padding: '0 24px',
                fontWeight: '700',
                fontSize: '14px',
                cursor: loading || !prompt.trim() ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
              }}
            >
              {loading ? 'Thinking...' : 'Ask AI ⚡'}
            </button>
          </form>
        </div>
      )}

      {/* TAB 2: TELUGU BROADCAST STUDIO */}
      {activeTab === 'broadcast' && (
        <div style={{ background: '#12161c', border: '1px solid #222a36', borderRadius: '16px', padding: '24px' }}>
          <h3 style={{ margin: '0 0 8px', fontSize: '18px' }}>📢 1-Click Telugu Driver Broadcast Composer</h3>
          <p style={{ margin: '0 0 20px', color: '#888', fontSize: '13px' }}>
            Type your message in simple English. AI will craft respectful, dialect-accurate Telugu text for Piler auto drivers.
          </p>

          <textarea
            value={broadcastPrompt}
            onChange={(e) => setBroadcastPrompt(e.target.value)}
            placeholder="e.g. Heavy rain near RTC Bus stand. ₹15 extra bonus per completed trip until 9:00 PM tonight. Please drive carefully."
            rows={3}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              background: '#161b22',
              border: '1px solid #30363d',
              borderRadius: '12px',
              padding: '14px',
              color: '#fff',
              fontSize: '14px',
              marginBottom: '14px',
              resize: 'vertical',
            }}
          />

          <button
            onClick={handleGenerateBroadcast}
            disabled={broadcastLoading || !broadcastPrompt.trim()}
            style={{
              background: '#00d68f',
              color: '#000',
              border: 'none',
              borderRadius: '10px',
              padding: '10px 20px',
              fontWeight: '700',
              fontSize: '13px',
              cursor: broadcastLoading ? 'not-allowed' : 'pointer',
              marginBottom: '24px',
            }}
          >
            {broadcastLoading ? 'Translating to Telugu...' : '✨ Generate Telugu Broadcast'}
          </button>

          {generatedBroadcast && (
            <div style={{ background: '#1a202c', border: '1px solid #2d3748', borderRadius: '14px', padding: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontSize: '12px', background: '#00d68f22', color: '#00d68f', padding: '3px 8px', borderRadius: '6px', fontWeight: '700' }}>
                  TELUGU NOTIFICATION PREVIEW
                </span>
                <span style={{ fontSize: '13px', fontWeight: '700', color: '#fff' }}>
                  {generatedBroadcast.shortTitle}
                </span>
              </div>

              <div style={{ fontSize: '16px', lineHeight: '1.7', color: '#fff', fontWeight: '500', marginBottom: '14px' }}>
                {generatedBroadcast.teluguText}
              </div>

              {generatedBroadcast.transliteration && (
                <div style={{ fontSize: '12px', color: '#888', fontStyle: 'italic', marginBottom: '18px' }}>
                  Pronunciation: "{generatedBroadcast.transliteration}"
                </div>
              )}

              <button
                onClick={handleSendFCM}
                disabled={!isSuperAdmin || sendingBroadcast}
                style={{
                  background: '#00d68f',
                  color: '#000',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '12px 24px',
                  fontWeight: '800',
                  fontSize: '14px',
                  cursor: (!isSuperAdmin || sendingBroadcast) ? 'not-allowed' : 'pointer',
                  opacity: isSuperAdmin ? 1 : 0.55,
                  width: '100%',
                }}
              >
                {!isSuperAdmin ? '🔒 Broadcast Restricted (Super Admin Only)' : sendingBroadcast ? 'Broadcasting...' : '🚀 Send Broadcast to All Online Annas Now'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* TAB 3: FRAUD SENTINEL */}
      {activeTab === 'fraud' && (
        <div style={{ background: '#12161c', border: '1px solid #222a36', borderRadius: '16px', padding: '24px' }}>
          <h3 style={{ margin: '0 0 8px', fontSize: '18px' }}>🛡️ Autonomous Fraud & Prank Investigator</h3>
          <p style={{ margin: '0 0 20px', color: '#888', fontSize: '13px' }}>
            Select a flagged rider or enter a user ID to generate an instant AI Risk Scorecard and remediation recommendation.
          </p>

          <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
            <select
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              style={{
                flex: 1,
                background: '#161b22',
                border: '1px solid #30363d',
                borderRadius: '10px',
                padding: '12px',
                color: '#fff',
                fontSize: '14px',
              }}
            >
              <option value="">-- Select Flagged Rider --</option>
              {users
                .filter((u) => u.isFlaggedForPrank || (u.prankCancellationStrikes && u.prankCancellationStrikes > 0))
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name || 'User'} ({u.phone || u.id}) — {u.prankCancellationStrikes || 0} Strikes
                  </option>
                ))}
            </select>

            <button
              onClick={() => handleRunAudit(selectedUser)}
              disabled={auditLoading || !selectedUser}
              style={{
                background: '#00d68f',
                color: '#000',
                border: 'none',
                borderRadius: '10px',
                padding: '0 20px',
                fontWeight: '700',
                fontSize: '13px',
                cursor: auditLoading || !selectedUser ? 'not-allowed' : 'pointer',
              }}
            >
              {auditLoading ? 'Auditing...' : '🔍 Run AI Risk Audit'}
            </button>
          </div>

          {auditResult && (
            <div style={{ background: '#1a202c', border: '1px solid #2d3748', borderRadius: '14px', padding: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div>
                  <span style={{ fontSize: '12px', color: '#888' }}>RISK ASSESSMENT</span>
                  <div style={{ fontSize: '22px', fontWeight: '800', color: auditResult.riskScore > 60 ? '#ef4444' : auditResult.riskScore > 30 ? '#f59e0b' : '#00d68f' }}>
                    Risk Score: {auditResult.riskScore} / 100 ({auditResult.riskLevel})
                  </div>
                </div>
                <div style={{ background: '#2d3748', padding: '6px 12px', borderRadius: '8px', fontSize: '13px', fontWeight: '700', color: '#00d68f' }}>
                  ACTION: {auditResult.recommendedAction}
                </div>
              </div>

              <p style={{ margin: '0 0 16px', color: '#ddd', fontSize: '14px', lineHeight: '1.6' }}>
                <strong>AI Rationale:</strong> {auditResult.rationale}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
